import React from 'react';
import { Shield, Eye, Lock, Database, Bell, Mail } from 'lucide-react';
import Header1 from '../common/Header1';
import Footer1 from '../common/Footer1';
import ScrollManager from '../layout/scrollManager';
import SideContactBar from '../layout/SideContactBar';
import SEOHead from '../layout/SEOHead';
import { SITE_URL } from '../../config/seo';

const Section = ({ title, children }) => (
    <div className="mb-10">
        <h2 className="text-xl font-bold text-gray-800 border-b border-gray-200 pb-3 mb-5">{title}</h2>
        {children}
    </div>
);

const privacySchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}/privacy#webpage`,
    name: 'Privacy Policy – Urban Shine Laundry',
    url: `${SITE_URL}/privacy`,
    description: 'Privacy Policy for Urban Shine Laundry. Learn how we collect, use, and protect your personal information.',
    datePublished: '2025-06-01',
    dateModified: '2025-06-01',
    breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: `${SITE_URL}/privacy` },
        ],
    },
};

const Privacy = () => {
    return (
        <ScrollManager>
            <SEOHead
                title="Privacy Policy – Urban Shine Laundry | New Town, Kolkata"
                description="Read the Privacy Policy of Urban Shine Laundry to understand how we collect, use, and protect your personal information when you use our laundry services."
                keywords="Urban Shine Laundry privacy policy, laundry service data privacy Kolkata"
                canonicalPath="/privacy"
                ogType="article"
                structuredData={privacySchema}
                noIndex={false}
                publishedTime="2025-06-01T00:00:00+05:30"
            />
            <Header1 />
            <SideContactBar />
            <br /><br /><br />
            <section className="py-16 md:py-12 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-neutral-100 text-neutral-700 rounded-full text-sm font-medium mb-4">Legal</span>
                        <h1 className="text-3xl md:text-5xl font-bold text-gray-800 mb-6">Privacy Policy</h1>
                        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                            At Urban Shine Laundry, your privacy matters to us. This policy explains what information we collect,
                            how we use it, and how we protect it.
                        </p>
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                        <div className="flex flex-col items-center text-center p-6 rounded-lg bg-green-50 hover:bg-green-100 transition-colors">
                            <div className="p-4 rounded-full bg-green-600 text-white mb-4">
                                <Eye size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">What We Collect</h3>
                            <p className="text-gray-600">Name, contact details, address, and order information needed to fulfil your requests.</p>
                        </div>
                        <div className="flex flex-col items-center text-center p-6 rounded-lg bg-neutral-50 hover:bg-neutral-100 transition-colors">
                            <div className="p-4 rounded-full bg-neutral-500 text-white mb-4">
                                <Lock size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">How We Protect It</h3>
                            <p className="text-gray-600">Your data is stored securely and never sold to third parties.</p>
                        </div>
                        <div className="flex flex-col items-center text-center p-6 rounded-lg bg-green-50 hover:bg-green-100 transition-colors">
                            <div className="p-4 rounded-full bg-green-600 text-white mb-4">
                                <Shield size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">Your Rights</h3>
                            <p className="text-gray-600">You can request access, correction, or deletion of your personal data at any time.</p>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-lg p-8 md:p-12 mb-16">
                        <Section title="1. Information We Collect">
                            <p className="text-gray-700 mb-4">
                                When you use our services — through our website, app, or by contacting us directly — we may collect the following information:
                            </p>
                            <ul className="space-y-3 text-gray-700 list-disc list-inside">
                                <li><strong>Personal identification:</strong> Name, phone number, and email address.</li>
                                <li><strong>Address details:</strong> Pickup and delivery address for order fulfilment.</li>
                                <li><strong>Order information:</strong> Services requested, order history, and preferences.</li>
                                <li><strong>Communication data:</strong> Messages or feedback you send us via WhatsApp, email, or our contact form.</li>
                                <li><strong>Device & usage data:</strong> IP address, browser type, and pages visited on our website (collected automatically via cookies).</li>
                            </ul>
                        </Section>

                        <Section title="2. How We Use Your Information">
                            <ul className="space-y-3 text-gray-700 list-disc list-inside">
                                <li>To process and fulfil your laundry orders (pickup scheduling, delivery coordination).</li>
                                <li>To contact you about your order status, updates, or service changes.</li>
                                <li>To send promotional offers or service updates (you may opt out at any time).</li>
                                <li>To improve our website, app, and overall service quality.</li>
                                <li>To comply with applicable legal obligations.</li>
                            </ul>
                        </Section>

                        <Section title="3. Information Sharing">
                            <p className="text-gray-700 mb-4">
                                We do <strong>not sell, rent, or trade</strong> your personal information to third parties. We may share your data only in these limited circumstances:
                            </p>
                            <ul className="space-y-3 text-gray-700 list-disc list-inside">
                                <li><strong>Service partners:</strong> Delivery staff who need your address to complete pickups/deliveries.</li>
                                <li><strong>Technology providers:</strong> Platforms that help us operate our app, website, or payment systems — bound by confidentiality agreements.</li>
                                <li><strong>Legal requirements:</strong> When required by law, court order, or government authority.</li>
                            </ul>
                        </Section>

                        <Section title="4. Data Retention">
                            <p className="text-gray-700">
                                We retain your personal information for as long as necessary to provide our services and comply with legal obligations.
                                Order history is typically retained for up to 3 years for accounting and dispute resolution purposes.
                                You may request deletion of your data at any time by contacting us.
                            </p>
                        </Section>

                        <Section title="5. Cookies">
                            <p className="text-gray-700 mb-4">
                                Our website may use cookies to enhance your browsing experience. Cookies are small files stored on your device that help us:
                            </p>
                            <ul className="space-y-3 text-gray-700 list-disc list-inside">
                                <li>Remember your preferences and session information.</li>
                                <li>Analyse website traffic and usage patterns.</li>
                                <li>Improve website performance and content relevance.</li>
                            </ul>
                            <p className="text-gray-700 mt-4">
                                You can control cookie settings through your browser. Disabling cookies may affect some website functionality.
                            </p>
                        </Section>

                        <Section title="6. Your Rights">
                            <p className="text-gray-700 mb-4">You have the right to:</p>
                            <ul className="space-y-3 text-gray-700 list-disc list-inside">
                                <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
                                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
                                <li><strong>Deletion:</strong> Request deletion of your personal data, subject to legal obligations.</li>
                                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time.</li>
                                <li><strong>Portability:</strong> Request transfer of your data in a machine-readable format.</li>
                            </ul>
                            <p className="text-gray-700 mt-4">
                                To exercise any of these rights, please contact us at{' '}
                                <a href="mailto:urbanshinelaundry46@gmail.com" className="text-green-600 hover:underline">
                                    urbanshinelaundry46@gmail.com
                                </a>.
                            </p>
                        </Section>

                        <Section title="7. Security">
                            <p className="text-gray-700">
                                We implement appropriate technical and organisational measures to protect your personal information against
                                unauthorised access, alteration, disclosure, or destruction. However, no method of data transmission over
                                the internet is 100% secure. If you have concerns about the security of your data, please contact us immediately.
                            </p>
                        </Section>

                        <Section title="8. Third-Party Links">
                            <p className="text-gray-700">
                                Our website may contain links to third-party websites (e.g., Google Maps). We are not responsible for the
                                privacy practices of those sites and encourage you to review their privacy policies before providing any personal information.
                            </p>
                        </Section>

                        <Section title="9. Changes to This Policy">
                            <p className="text-gray-700">
                                We may update this Privacy Policy from time to time. The latest version will always be posted on our website.
                                Continued use of our services after changes are posted constitutes your acceptance of the updated policy.
                            </p>
                        </Section>

                        <Section title="10. Contact Us">
                            <p className="text-gray-700 mb-4">
                                If you have any questions or concerns about this Privacy Policy, please reach out to us:
                            </p>
                            <ul className="space-y-2 text-gray-700">
                                <li>
                                    <strong>Email:</strong>{' '}
                                    <a href="mailto:urbanshinelaundry46@gmail.com" className="text-green-600 hover:underline">
                                        urbanshinelaundry46@gmail.com
                                    </a>
                                </li>
                                <li><strong>Phone:</strong> <a href="tel:+918450903882" className="text-green-600 hover:underline">+91 84509 03882</a></li>
                                <li><strong>Address:</strong> DF 05, Street Number 316, DF Block, New Town, Kolkata 700156</li>
                            </ul>
                        </Section>

                        <div className="mt-8 p-6 bg-green-50 border-l-4 border-green-600 rounded-r-lg">
                            <div className="flex items-start">
                                <Database size={24} className="text-green-600 mr-4 mt-1 flex-shrink-0" />
                                <div>
                                    <h4 className="text-lg font-bold text-gray-800 mb-2">Data Protection Commitment</h4>
                                    <p className="text-gray-700">
                                        Urban Shine Laundry is committed to protecting your privacy and handling your data responsibly.
                                        We will never sell your information to advertisers or data brokers.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="text-center mt-10 text-gray-500 text-sm">
                            <p>Last Updated: June 2025</p>
                        </div>
                    </div>
                </div>
            </section>
            <Footer1 />
        </ScrollManager>
    );
};

export default Privacy;
